/* istanbul ignore file */
import { createStore } from 'vuex';
import VuexPersistence from 'vuex-persist';
import localforage from 'localforage';
import _get from 'lodash/get';
import useToRaw from '@/hooks/common/useToRaw';

import advancedSearch from '@/store/modules/advancedSearch';
import auth from '@/store/modules/auth';
import breadcrumbs from '@/store/modules/breadcrumbs';
import cached from '@/store/modules/cached';
import customer from '@/store/modules/customer';
import editObject from '@/store/modules/editObject';
import glAccount from '@/store/modules/glAccount';
import home from '@/store/modules/home';
import menu from '@/store/modules/menu';
import meta from '@/store/modules/meta';
import order from '@/store/modules/order';
import permissions from '@/store/modules/permissions';
import preferences from '@/store/modules/preferences';
import search from '@/store/modules/search';
import arTran from '@/store/modules/arTran';
import translations from '@/store/modules/translations';
import notify from '@/store/modules/notify';

const { deepToRaw } = useToRaw();

const canSaveSiteCashdrawer = (state) => {
  const sysParams = _get(state, 'meta.clientSetupData.systemparameters');
  if (!sysParams) return false;
  return _get(sysParams, 'save_site_cashdrawer_on_login') === 'Y';
};

localforage.config({
  name: 'loft-ui-local',
  driver: [
    localforage.INDEXEDDB,
    localforage.WEBSQL,
    localforage.LOCALSTORAGE,
  ],
  version: 1.0,
  storeName: 'loft-ui-vuex',
});

const vuexPersist = new VuexPersistence({
  storage: localforage,
  asyncStorage: true,
  key: 'loft-ui-vuex',
  modules: [
    'advancedSearch',
    'auth',
    'cached',
    'glAccount',
    'menu',
    'meta',
    'permissions',
    'preferences',
    'translations',
  ],

  filter: ({ type }) => {
    const whitelistedMutations = [
      'auth/loginSites',
      'auth/login',
      'auth/logout',
      'auth/update_token',
      'auth/authenticated',
      'auth/set_cash_drawer',
      'cached/initialize_cached_state',
      'cached/cache_screen_data',
      'cached/set_data',
      'cached/update_cached_search',
      'cached/update_additional_search_cache',
      'cached/update_cached_search_results',
      'cached/remove_from_cached_search',
      'cached/refresh_cached_search',
      'cached/clear_active_request',
      'glAccount/set_cached_gl_accounts',
      'glAccount/set_in_flight',
      'menu/set_menu_tree',
      'menu/toggle_state',
      'meta/update_schema',
      'meta/update_client_setup_data',
      'meta/set_uql_constants',
      'permissions/set_initial_permissions',
      'preferences/set_initial_preferences',
      'preferences/update_user_preference',
      'preferences/delete_user_preference',
      'translations/setTranslations',
    ];
    return whitelistedMutations.includes(type);
  },
  reducer: (state) => {
    const data = {
      advancedSearch: { ...state.advancedSearch, recentQueries: {} },
      auth: {
        ...state.auth,
        // Do not persist timeoutId (set on application load)
        timeoutId: null,
        // Following handled via loft-workstation cookie
        ...(canSaveSiteCashdrawer(state) && {
          loggedInSiteId: null,
          cashDrawerId: null,
        }),
        // Make certain any previous asyncrequests are cleared:
        deviceDialogModel: {},
        // Do not persist modal state
        showInstallmentCalculatorModal: false,
        showDeviceDialogModal: false,
        showStoreCardModal: false,
      },
      cached: { ...state.cached, previousScreenData: {}, activeRequests: {} },
      glAccount: { ...state.glAccount },
      menu: { ...state.menu, drawer: false },
      meta: { ...state.meta },
      permissions: { ...state.permissions },
      preferences: { ...state.preferences },
      translations: { ...state.translations },
    };

    // Vue 3's use of proxy objects
    // Need to remove proxy objects to be persisted in local storage
    // https://github.com/dexie/Dexie.js/issues/1237
    return deepToRaw(data);
  },
});

const store = createStore({
  modules: {
    advancedSearch,
    arTran,
    auth,
    breadcrumbs,
    cached,
    customer,
    editObject,
    glAccount,
    home,
    menu,
    meta,
    notify,
    order,
    preferences,
    permissions,
    search,
    translations,
  },
  plugins: [
    vuexPersist.plugin,
  ],
});

export default store;
